import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Box, MessageSquare, Phone, Calendar, TrendingUp, Users, Star } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [portfolioStats, setPortfolioStats] = useState({ total: 0, published: 0, draft: 0 });
    const [eventStats, setEventStats] = useState({ total: 0, active: 0, inactive: 0 });
    const [reviewStats, setReviewStats] = useState({ total: 0, visible: 0, hidden: 0, averageRating: 0 });
    const [contactStats, setContactStats] = useState({ total: 0, new: 0, consulting: 0, completed: 0 });
    const [recentActivities, setRecentActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is authenticated
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                navigate('/admin/login');
                return;
            }
            
            // Fetch stats only if user is authenticated
            fetchStats();
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [navigate]);

    const fetchStats = async () => {
        try {
            // 포트폴리오 통계
            const portfoliosQuery = query(collection(db, 'portfolios'), orderBy('created_at', 'desc'));
            const portfolioSnapshot = await getDocs(portfoliosQuery);
            const portfolioData = [];
            portfolioSnapshot.forEach((doc) => {
                portfolioData.push({ id: doc.id, ...doc.data() });
            });

            if (portfolioData) {
                setPortfolioStats({
                    total: portfolioData.length,
                    published: portfolioData.filter((p) => p.is_published).length,
                    draft: portfolioData.filter((p) => !p.is_published).length
                });
            }

            // 이벤트 통계
            const eventsQuery = query(collection(db, 'events'), orderBy('created_at', 'desc'));
            const eventSnapshot = await getDocs(eventsQuery);
            const eventData = [];
            eventSnapshot.forEach((doc) => {
                eventData.push({ id: doc.id, ...doc.data() });
            });

            if (eventData) {
                setEventStats({
                    total: eventData.length,
                    active: eventData.filter((e) => e.is_active).length,
                    inactive: eventData.filter((e) => !e.is_active).length
                });
            }

            // 리뷰 통계
            const reviewsQuery = query(collection(db, 'reviews'), orderBy('created_at', 'desc'));
            const reviewSnapshot = await getDocs(reviewsQuery);
            const reviewData = [];
            reviewSnapshot.forEach((doc) => {
                reviewData.push({ id: doc.id, ...doc.data() });
            });

            if (reviewData) {
                const averageRating = reviewData.length > 0
                    ? reviewData.reduce((sum, review) => sum + review.rating, 0) / reviewData.length
                    : 0;

                setReviewStats({
                    total: reviewData.length,
                    visible: reviewData.filter((r) => !r.is_hidden).length,
                    hidden: reviewData.filter((r) => r.is_hidden).length,
                    averageRating
                });
            }

            // 문의사항 통계
            const contactsQuery = query(collection(db, 'contacts'), orderBy('created_at', 'desc'));
            const contactSnapshot = await getDocs(contactsQuery);
            const contactData = [];
            contactSnapshot.forEach((doc) => {
                contactData.push({ id: doc.id, ...doc.data() });
            });

            if (contactData) {
                setContactStats({
                    total: contactData.length,
                    new: contactData.filter((c) => c.status === 'new').length,
                    consulting: contactData.filter((c) => c.status === 'consulting').length,
                    completed: contactData.filter((c) => c.status === 'completed').length
                });
            }

            // 최근 활동
            const activities = [];

            // 최근 리뷰
            const recentReviewsQuery = query(collection(db, 'reviews'), orderBy('created_at', 'desc'), limit(3));
            const recentReviewsSnapshot = await getDocs(recentReviewsQuery);
            const recentReviews = [];
            recentReviewsSnapshot.forEach((doc) => {
                recentReviews.push({ id: doc.id, ...doc.data() });
            });

            if (recentReviews) {
                activities.push(...recentReviews.map((review) => ({
                    type: 'review',
                    title: `${review.user_name}님이 리뷰를 작성했습니다`,
                    subtitle: `${review.rating}점`,
                    time: review.created_at,
                    icon: Star
                })));
            }

            // 최근 문의
            const recentContactsQuery = query(collection(db, 'contacts'), orderBy('created_at', 'desc'), limit(3));
            const recentContactsSnapshot = await getDocs(recentContactsQuery);
            const recentContacts = [];
            recentContactsSnapshot.forEach((doc) => {
                recentContacts.push({ id: doc.id, ...doc.data() });
            });

            if (recentContacts) {
                activities.push(...recentContacts.map((contact) => ({
                    type: 'contact',
                    title: `${contact.name}님이 문의를 남겼습니다`,
                    subtitle: contact.construction_type,
                    time: contact.created_at,
                    icon: Phone
                })));
            }

            // 시간순 정렬
            setRecentActivities(activities.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 5));

        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const statsCards = [
        {
            title: '포트폴리오',
            value: portfolioStats?.total || 0,
            subtitle: `게시됨: ${portfolioStats?.published || 0}`,
            icon: Box,
            color: 'bg-blue-500',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600'
        },
        {
            title: '활성 이벤트',
            value: eventStats?.active || 0,
            subtitle: `전체: ${eventStats?.total || 0}`,
            icon: Calendar,
            color: 'bg-green-500',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600'
        },
        {
            title: '고객 리뷰',
            value: reviewStats?.total || 0,
            subtitle: `평균: ${reviewStats?.averageRating?.toFixed(1) || 0}점`,
            icon: MessageSquare,
            color: 'bg-yellow-500',
            bgColor: 'bg-yellow-50',
            textColor: 'text-yellow-600'
        },
        {
            title: '신규 문의',
            value: contactStats?.new || 0,
            subtitle: `전체: ${contactStats?.total || 0}`,
            icon: Phone,
            color: 'bg-purple-500',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600'
        }
    ];

    const quickActions = [
        {
            title: '포트폴리오 관리',
            description: '프로젝트 포트폴리오를 추가하고 관리합니다',
            icon: Box,
            href: '/admin/portfolios',
            color: 'bg-blue-500'
        },
        {
            title: '이벤트 관리',
            description: '프로모션과 이벤트를 관리합니다',
            icon: Calendar,
            href: '/admin/events',
            color: 'bg-green-500'
        },
        {
            title: '리뷰 관리',
            description: '고객 리뷰를 확인하고 관리합니다',
            icon: MessageSquare,
            href: '/admin/reviews',
            color: 'bg-yellow-500'
        },
        {
            title: '고객 문의',
            description: '고객 문의사항을 처리합니다',
            icon: Phone,
            href: '/admin/contacts',
            color: 'bg-purple-500'
        }
    ];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* 헤더 */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">관리자 대시보드</h1>
                <p className="text-sm text-gray-600 mt-1">비즈니스 현황을 한눈에 확인하세요</p>
            </div>

            {/* 통계 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statsCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">{card.title}</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                                    <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
                                </div>
                                <div className={`${card.bgColor} p-3 rounded-lg`}>
                                    <Icon className={`w-6 h-6 ${card.textColor}`} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 빠른 작업 */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center mb-4">
                        <TrendingUp className="w-5 h-5 text-gray-600 mr-2" />
                        <h2 className="text-lg font-semibold text-gray-900">빠른 작업</h2>
                    </div>
                    <div className="space-y-3">
                        {quickActions.map((action, index) => {
                            const Icon = action.icon;
                            return (
                                <a
                                    key={index}
                                    href={action.href}
                                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                                >
                                    <div className={`${action.color} p-2 rounded-lg mr-3`}>
                                        <Icon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-gray-900">{action.title}</h3>
                                        <p className="text-xs text-gray-500">{action.description}</p>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>

                {/* 최근 활동 */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center mb-4">
                        <Users className="w-5 h-5 text-gray-600 mr-2" />
                        <h2 className="text-lg font-semibold text-gray-900">최근 활동</h2>
                    </div>
                    <div className="space-y-3">
                        {recentActivities && recentActivities.length > 0 ? (
                            recentActivities.map((activity, index) => {
                                const Icon = activity.icon;
                                return (
                                    <div key={index} className="flex items-center p-3 rounded-lg bg-gray-50">
                                        <div className="bg-white p-2 rounded-lg mr-3 border">
                                            <Icon className="w-4 h-4 text-gray-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-sm font-medium text-gray-900">{activity.title}</h3>
                                            <p className="text-xs text-gray-500">{activity.subtitle}</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {new Date(activity.time).toLocaleString('ko-KR')}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8">
                                <BarChart3 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">최근 활동이 없습니다</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}